// The lobby's parade under the medsnapp look (3 Nov 2026): three or four of MedSnapp's cast, Bleep first (it starts every
// battle), beside the "medics in the lobby" figure. Renders nothing in any other look.
//
// 4 Nov 2026: they drop in from the top one by one, then take turns, one at a time (or two neighbours for a high five):
// a wave, a little dance, a high five, a peek, a wink. Never everyone at once, never the same move twice in a row.
// Timed on the cast's one clock. Reduced motion: they stand still.
import { useEffect, useState } from 'react';
import { useLook } from '@/look';
import { useReducedMotion } from '@/motion/reduced';
import { CastMascot } from './CastMascot';
import { LOBBY_PARADE } from './cast';
import { ACTS, paradeTurn, type ActName, type ParadeTurn } from './acts';
import { after } from './clock';

/** The gap between drop-ins, then the pause before the first turn and between turns, in ms. */
export const PARADE = { dropGap: 420, firstTurn: 1400, gap: [1600, 2800] as [number, number] } as const;

type Cues = Record<number, { act: ActName; n: number }>;

/** The parade itself, in any look (the gallery shows it too). */
export function ParadeRow({ names, size, className = '' }: { names: readonly string[]; size: number; className?: string }) {
  const reduce = useReducedMotion();
  const [cues, setCues] = useState<Cues>({});
  const count = names.length;

  useEffect(() => {
    if (reduce || count < 1) return;
    let last: ParadeTurn | undefined;
    let n = 0;
    let cancel = () => {};
    const turn = () => {
      const next = paradeTurn(count, last);
      if (!next) return;
      last = next; n += 1;
      const id = n;
      setCues((c) => {
        const out = { ...c };
        next.who.forEach((who, i) => { out[who] = { act: next.acts[i], n: id }; });
        return out;
      });
      const ms = Math.max(...next.acts.map((a) => ACTS[a].ms));
      cancel = after(ms + PARADE.gap[0] + Math.random() * (PARADE.gap[1] - PARADE.gap[0]), turn);
    };
    cancel = after(count * PARADE.dropGap + ACTS['drop-in'].ms + PARADE.firstTurn, turn);
    return () => cancel();
  }, [reduce, count]);

  return (
    <div data-part="cast-parade" aria-hidden="true" className={`flex items-end gap-2 ${className}`}>
      {names.map((name, i) => (
        <CastMascot key={name} name={name} mood="idle" size={size} act={reduce ? null : 'drop-in'} delay={i * PARADE.dropGap} cue={cues[i]} />
      ))}
    </div>
  );
}

export function CastParade({ size, count = LOBBY_PARADE.length, className = '' }: { size: number; count?: number; className?: string }) {
  const { look } = useLook();
  if (look !== 'medsnapp') return null;
  return <ParadeRow names={LOBBY_PARADE.slice(0, count)} size={size} className={className} />;
}
